import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useColorMode,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  useToast,
} from "@chakra-ui/react";
import {
  FaCoins,
  FaGift,
  FaUsers,
  FaVideo,
  FaHandshake,
  FaShareAlt,
  FaUserPlus,
  FaCalendarCheck,
  FaHourglassHalf,
  FaTrophy,
} from "react-icons/fa";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { GET_PUBLIC_REWARD_TOKEN_CONFIG } from "../rewards/queries";
import { getColor } from "../../brandConfig";

const MY_REWARD_BALANCE = gql`
  query MyRewardBalance {
    myRewardBalance {
      clientId
      clientName
      activeTokens
      totalValue
      expiringWithin30Days
    }
  }
`;

const MY_REWARD_TOKENS = gql`
  query MyRewardTokens {
    myRewardTokens {
      id
      tokenNumber
      status
      value
      issueReason
      issueDetails
      issuedAt
      expiresAt
      redeemedAt
      redeemedFor
    }
  }
`;

const MY_REWARD_TRANSACTIONS = gql`
  query MyRewardTransactions {
    myRewardTransactions {
      id
      tokenNumber
      transactionType
      reason
      details
      performedAt
      newStatus
    }
  }
`;

const DEFAULT_TOKEN_VALUE = 50; // fallback AU$50 per token

const earnActivities = [
  {
    icon: FaVideo,
    title: "Register for Webinar",
    tokens: 1,
    description: "Earn 1 BB Token just for registering for a live webinar event",
    color: "#3B82F6",
  },
  {
    icon: FaCalendarCheck,
    title: "Show Up Live",
    tokens: 1,
    description: "Attend the live webinar and earn another BB Token",
    color: "#10B981",
  },
  {
    icon: FaHourglassHalf,
    title: "Stay Till End",
    tokens: 1,
    description: "Stay for the entire webinar to earn a bonus BB Token",
    color: "#F59E0B",
  },
  {
    icon: FaHandshake,
    title: "Refer a Friend",
    tokens: 1,
    description: "Refer someone who registers and you both earn a BB Token (max 3 per event)",
    color: "#EC4899",
  },
  {
    icon: FaShareAlt,
    title: "Social Media Post",
    tokens: 1,
    description: "Post about the event on social media with a verified screenshot",
    color: "#8B5CF6",
  },
  {
    icon: FaUserPlus,
    title: "Subscribe Bonus",
    tokens: 2,
    description: "Sign up as a subscriber and receive 2 BB Tokens as a welcome bonus",
    color: "#9333EA",
  },
];

const Rewards = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.9) 50%, rgba(255,255,255,0.95) 100%)"
      : "linear-gradient(135deg, rgba(20,20,20,0.8) 0%, rgba(30,30,30,0.6) 50%, rgba(20,20,20,0.8) 100%)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const textMuted = colorMode === "light" ? "gray.500" : "gray.500";

  const { data: balanceData, loading: balanceLoading } = useQuery(MY_REWARD_BALANCE, {
    onError: () => {}, // Silently handle auth errors for non-logged-in users
  });
  const { data: tokensData, loading: tokensLoading } = useQuery(MY_REWARD_TOKENS, {
    onError: () => {},
  });
  const { data: transactionsData, loading: transactionsLoading } = useQuery(MY_REWARD_TRANSACTIONS, {
    onError: () => {},
  });
  const { data: configData } = useQuery(GET_PUBLIC_REWARD_TOKEN_CONFIG);
  const BB_TOKEN_VALUE = configData?.publicRewardTokenConfig?.tokenValue || DEFAULT_TOKEN_VALUE;

  const balance = balanceData?.myRewardBalance;
  const tokens = tokensData?.myRewardTokens || [];
  const transactions = transactionsData?.myRewardTransactions || [];

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE": return "green";
      case "REDEEMED": return "blue";
      case "EXPIRED": return "red";
      case "CANCELLED": return "gray";
      default: return "gray";
    }
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={16}>
        <VStack spacing={12} align="stretch">
          {/* Hero */}
          <Box textAlign="center" py={8}>
            <Badge
              bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
              color="white"
              fontSize="md"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="600"
              mb={6}
              boxShadow="0 4px 16px rgba(147, 51, 234, 0.4)"
            >
              <Icon as={FaCoins} mr={2} />
              BB Token Rewards
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="800"
              mb={4}
              color={textPrimary}
              bgGradient="linear(to-r, #9333EA, #F59E0B)"
              bgClip="text"
            >
              Earn. Engage. Save.
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={textSecondary} maxW="800px" mx="auto">
              BB Tokens reward your engagement with the Business Builder ecosystem.
              Each token is worth <strong>AU${BB_TOKEN_VALUE}</strong> and can be used to pay for your monthly subscription.
            </Text>
            <Text fontSize="md" color={textMuted} mt={3} fontStyle="italic">
              2 BB Tokens = 1 month free ($97/mo subscription)
            </Text>
          </Box>

          {/* Balance Cards */}
          {balance && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Card
                bg="linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)"
                borderWidth="2px"
                borderColor="rgba(147, 51, 234, 0.4)"
                borderRadius="xl"
              >
                <CardBody textAlign="center" py={8}>
                  <Icon as={FaCoins} w={10} h={10} color="#F59E0B" mb={3} />
                  <Stat>
                    <StatLabel color={textSecondary} fontSize="md">Token Balance</StatLabel>
                    <StatNumber color={textPrimary} fontSize="4xl">{balance.activeTokens}</StatNumber>
                    <StatHelpText color="#10B981" fontWeight="600">
                      AU${balance.activeTokens * BB_TOKEN_VALUE} value
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card
                bg={cardGradientBg}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="xl"
              >
                <CardBody textAlign="center" py={8}>
                  <Icon as={FaGift} w={10} h={10} color="#9333EA" mb={3} />
                  <Stat>
                    <StatLabel color={textSecondary} fontSize="md">Free Months Available</StatLabel>
                    <StatNumber color={textPrimary} fontSize="4xl">
                      {Math.floor(balance.activeTokens / 2)}
                    </StatNumber>
                    <StatHelpText color={textMuted}>
                      2 tokens = 1 month ($97)
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card
                bg={cardGradientBg}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="xl"
              >
                <CardBody textAlign="center" py={8}>
                  <Icon as={FaHourglassHalf} w={10} h={10} color="#F59E0B" mb={3} />
                  <Stat>
                    <StatLabel color={textSecondary} fontSize="md">Expiring Soon</StatLabel>
                    <StatNumber color={balance.expiringWithin30Days > 0 ? "#EF4444" : textPrimary} fontSize="4xl">
                      {balance.expiringWithin30Days}
                    </StatNumber>
                    <StatHelpText color={textMuted}>
                      Within 30 days
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>
          )}

          {balanceLoading && (
            <Flex justify="center" py={8}>
              <Spinner size="xl" color="#9333EA" />
            </Flex>
          )}

          {/* How to Earn */}
          <Card
            bg={cardGradientBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="2xl"
            boxShadow="0 8px 32px rgba(0,0,0,0.2)"
          >
            <CardHeader>
              <Flex align="center">
                <Icon as={FaTrophy} w={7} h={7} color="#F59E0B" mr={3} />
                <Heading size="lg" color={textPrimary}>
                  How to Earn BB Tokens
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {earnActivities.map((activity, i) => (
                  <Box
                    key={i}
                    p={5}
                    bg={`rgba(${activity.color === "#3B82F6" ? "59,130,246" : activity.color === "#10B981" ? "16,185,129" : activity.color === "#F59E0B" ? "245,158,11" : activity.color === "#EC4899" ? "236,72,153" : activity.color === "#8B5CF6" ? "139,92,246" : "147,51,234"}, 0.1)`}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={`${activity.color}40`}
                    transition="transform 0.2s"
                    _hover={{ transform: "translateY(-2px)" }}
                  >
                    <Flex align="center" mb={3}>
                      <Icon as={activity.icon} w={8} h={8} color={activity.color} mr={3} />
                      <Box>
                        <Text fontWeight="700" color={textPrimary} fontSize="md">
                          {activity.title}
                        </Text>
                        <Badge
                          bg={activity.color}
                          color="white"
                          borderRadius="full"
                          px={3}
                          fontSize="xs"
                        >
                          +{activity.tokens} {activity.tokens === 1 ? "Token" : "Tokens"}
                        </Badge>
                      </Box>
                    </Flex>
                    <Text fontSize="sm" color={textSecondary}>
                      {activity.description}
                    </Text>
                    <Text fontSize="xs" color={textMuted} mt={2} fontStyle="italic">
                      Worth AU${activity.tokens * BB_TOKEN_VALUE}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>

              <Box
                mt={8}
                p={4}
                bg="rgba(147, 51, 234, 0.1)"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="rgba(147, 51, 234, 0.3)"
                textAlign="center"
              >
                <Text fontSize="md" color={textSecondary}>
                  <strong>Max per event:</strong> 5 tokens (AU$250) + up to 3 referral tokens (AU$150)
                </Text>
                <Text fontSize="sm" color={textMuted} mt={1}>
                  Tokens expire after 12 months. Use them before they're gone!
                </Text>
              </Box>
            </CardBody>
          </Card>

          {/* Token History */}
          {tokens.length > 0 && (
            <Card
              bg={cardGradientBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.2)"
            >
              <CardHeader>
                <Flex align="center">
                  <Icon as={FaCoins} w={6} h={6} color="#9333EA" mr={3} />
                  <Heading size="lg" color={textPrimary}>
                    Your BB Tokens
                  </Heading>
                </Flex>
              </CardHeader>
              <CardBody overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={textMuted}>#</Th>
                      <Th color={textMuted}>Reason</Th>
                      <Th color={textMuted}>Status</Th>
                      <Th color={textMuted}>Issued</Th>
                      <Th color={textMuted}>Expires</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tokens.map((token: any) => (
                      <Tr key={token.id}>
                        <Td color={textPrimary} fontWeight="600">#{token.tokenNumber}</Td>
                        <Td color={textSecondary}>{token.issueReason}</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(token.status)} borderRadius="full" px={3}>
                            {token.status}
                          </Badge>
                        </Td>
                        <Td color={textSecondary}>{formatDate(token.issuedAt)}</Td>
                        <Td color={textSecondary}>{formatDate(token.expiresAt)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* Transaction History */}
          {transactions.length > 0 && (
            <Card
              bg={cardGradientBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.2)"
            >
              <CardHeader>
                <Heading size="md" color={textPrimary}>
                  Transaction History
                </Heading>
              </CardHeader>
              <CardBody overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={textMuted}>Token</Th>
                      <Th color={textMuted}>Type</Th>
                      <Th color={textMuted}>Reason</Th>
                      <Th color={textMuted}>Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {transactions.slice(0, 20).map((tx: any) => (
                      <Tr key={tx.id}>
                        <Td color={textPrimary}>#{tx.tokenNumber}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              tx.transactionType === "ISSUED" ? "green" :
                              tx.transactionType === "REDEEMED" ? "blue" :
                              tx.transactionType === "EXPIRED" ? "red" : "gray"
                            }
                            borderRadius="full"
                            px={3}
                          >
                            {tx.transactionType}
                          </Badge>
                        </Td>
                        <Td color={textSecondary}>{tx.reason}</Td>
                        <Td color={textSecondary}>{formatDate(tx.performedAt)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6366F1 100%)"
            p={10}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaCoins} w={10} h={10} mb={3} />
            <Heading size="xl" mb={3}>
              Start Earning BB Tokens Today
            </Heading>
            <Text fontSize="lg" mb={6} maxW="600px" mx="auto">
              Join our next webinar to earn up to 5 BB Tokens (AU$250).
              That's more than 2 months free on your subscription!
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#9333EA"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255,255,255,0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => window.location.href = "/live-event/register"}
              >
                Register for Next Webinar
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255,255,255,0.15)",
                  transform: "translateY(-2px)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => window.location.href = "/my-rewards"}
              >
                View My Tokens
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Rewards;
